import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DEFAULT_PAGE_LIMIT,
  ENDPOINTS,
  SEARCH_WEAKNESS_SIGNALS,
  type SearchWeaknessItem,
  type SearchWeaknessSignal,
} from '@clm/shared-types';

import { fetchSearchWeakness } from '../../api/quality';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PanelProblem, Tile } from './panel';
import { FORBIDDEN_COPY, usePaging, usePanel } from './panel-state';

/**
 * (a) Search weakness — where clause search is weak, read from `clause_search_log`.
 *
 * Every tile is one named SQL aggregate (`quality-queries.ts` S1); every row is a
 * normalised query with its runs in the window. Nothing here is computed in the
 * browser, and no row has an action: the row is the fact. Alias proposals from
 * the zero-result signal are a separate story; the copy names no lane.
 */

const PANEL = 'Search weakness';
const ENDPOINT_PATH = ENDPOINTS['quality-search-weakness'].path;

const SIGNAL_LABEL: Readonly<Record<SearchWeaknessSignal, string>> = {
  zero_result: 'No result',
  unselected: 'Shown, never selected',
  drift: 'Rank drift',
  not_relevant_top: 'NotRelevant at rank 1',
};

function clauseLink(code: string | null) {
  if (code === null) return '—';
  return <Link to={`/clauses/${encodeURIComponent(code)}`}>{code}</Link>;
}

function columnsFor(signal: SearchWeaknessSignal): readonly DataTableColumn<SearchWeaknessItem>[] {
  const base: DataTableColumn<SearchWeaknessItem>[] = [
    { id: 'query', header: 'Query', cell: (r) => r.sample_query_text },
    { id: 'runs', header: 'Runs', cell: (r) => r.runs, cellClassName: 'num' },
    { id: 'first', header: 'First seen', cell: (r) => r.first_seen_utc },
    { id: 'last', header: 'Last seen', cell: (r) => r.last_seen_utc },
    { id: 'top', header: 'Top clause', cell: (r) => clauseLink(r.top_clause_code) },
  ];
  if (signal === 'drift') {
    base.push({ id: 'distinct', header: 'Distinct top clauses', cell: (r) => r.distinct_top_clauses, cellClassName: 'num' });
  }
  if (signal === 'not_relevant_top') {
    base.push({ id: 'nr', header: 'NotRelevant', cell: (r) => r.not_relevant_judgements, cellClassName: 'num' });
  }
  return base;
}

export function SearchWeaknessPanel({ windowDays }: { readonly windowDays: number }) {
  const [signal, setSignal] = useState<SearchWeaknessSignal>('zero_result');
  const paging = usePaging();
  const { state, reload } = usePanel(
    (abort) =>
      fetchSearchWeakness(
        {
          window_days: windowDays,
          signal,
          page: paging.cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor: paging.cursor },
        },
        abort,
      ),
    [windowDays, signal, paging.cursor],
  );

  return (
    <section className="card qpanel" aria-labelledby="q-weakness">
      <h3 id="q-weakness">{PANEL}</h3>
      <p>
        Four signals from the clause-search log, over the window: queries that found nothing, queries shown a
        result nobody ever picked (repeatedly), the same query ranking a different clause first, and a top
        result Legal judged NotRelevant. Each number is a named aggregate — its definition is the SQL behind
        it, not a browser calculation.
      </p>

      <label className="check">
        Signal
        <select
          aria-label="Signal"
          value={signal}
          onChange={(event) => {
            setSignal(event.target.value as SearchWeaknessSignal);
            paging.reset();
          }}
        >
          {SEARCH_WEAKNESS_SIGNALS.map((s) => (
            <option key={s} value={s}>
              {SIGNAL_LABEL[s]}
            </option>
          ))}
        </select>
      </label>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={FORBIDDEN_COPY} />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <div className="totals">
            <Tile label="Queries" value={state.response.totals.queries} sub={`${String(state.response.totals.unique_queries)} unique`} />
            <Tile label="Zero-result" value={state.response.totals.zero_result} tone={state.response.totals.zero_result > 0 ? 'bad' : undefined} />
            <Tile label="Never selected" value={state.response.totals.unselected} />
            <Tile
              label="Drifting"
              value={state.response.totals.drifting}
              sub="same query, different top clause"
              tone={state.response.totals.drifting > 0 ? 'warn' : undefined}
            />
            <Tile label="Judged wrong at top" value={state.response.totals.not_relevant_top} />
          </div>

          <DataTable
            caption={`Weak queries — ${SIGNAL_LABEL[state.response.signal]}`}
            columns={columnsFor(state.response.signal)}
            rows={state.response.items}
            rowKey={(r) => r.query_key}
            empty={
              state.response.signal === 'not_relevant_top' && state.response.totals.not_relevant_top === 0 ? (
                <p>No NotRelevant judgements recorded yet.</p>
              ) : (
                <p>
                  <strong>No weak queries in the last {state.response.totals.window_days} days.</strong> Every logged search
                  returned a result that was selected.
                </p>
              )
            }
          />
          <CursorPager
            next_cursor={state.response.page.next_cursor}
            historyDepth={paging.historyDepth}
            onNext={() => {
              if (state.response.page.next_cursor !== null) paging.next(state.response.page.next_cursor);
            }}
            onPrevious={paging.previous}
            pageNumber={paging.page}
            returnedCount={state.response.page.returned_count}
            totalInScope={null}
          />
        </>
      ) : null}
    </section>
  );
}
